// src/App.js
import React from 'react';
import { HelmetProvider, Helmet } from "react-helmet-async";
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from './components/common/ScrollToTop';
import { useAuth } from './hooks/useAuth';

// Layout Components
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import PopupInquiry from './components/PopupInquiry';
import WhatsAppButton from './components/common/WhatsAppButton';

// Pages
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import ProjectsPage from './pages/ProjectsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogDetail from './pages/BlogDetail';
import CareerPage from './pages/CareerPage';
import ApplyPage from './pages/ApplyPage';
import TeamPage from './pages/TeamPage';
import FAQPage from './pages/FAQPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import SitemapPage from './pages/SitemapPage';
import WatchDemo from './pages/WatchDemo';

// Admin
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';

// Components
import AuthDebug from './components/AuthDebug';

import { Outlet } from "react-router-dom";
import './App.css';

// Protected Route Component - Updated for Zustand
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, hasRole, loading, user } = useAuth();
  
  // ✅ Console log removed for production
  
  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Verifying authentication...</p>
      </div>
    );
  }
  
  // Check if authenticated
  if (!isAuthenticated) {
    // ✅ Console log removed
    return <Navigate to="/admin/login" replace />;
  }
  
  // Check role if required
  if (requiredRole) {
    const userHasRole = hasRole(requiredRole);
    
    // Temporarily allow access even without admin role for testing
    if (!userHasRole) {
      // ✅ Console log removed
      // Uncomment below to enforce role check
      // return <Navigate to="/admin/dashboard" replace />;
    }
  }
  
  // ✅ Console log removed
  return children;
};

// Layout Component
// Renders sitewide default SEO tags. These are the fallback for any route
// that doesn't declare its own <Helmet> (e.g. sitemap, watch-demo, apply);
// pages that DO declare their own override these automatically since
// Helmet resolves nested/later instances over outer/earlier ones for the
// same tag. Keeping description/canonical/robots/OG out of public/index.html
// entirely avoids duplicate, conflicting tags in the rendered DOM.
function Layout() {
  return (
    <>
      <Helmet>
        <title>ZustandTech | Global Software Development Company | React, AI, Cloud & Mobile App Experts</title>
        <meta
          name="description"
          content="ZustandTech (Zustand Tech Software Private Limited) is a global software development company and one of the best software companies in India, specializing in React.js, Node.js, AI solutions, cloud computing, SaaS platforms, and mobile app development."
        />
        <meta name="keywords" content="ZustandTech, Zustand Tech Software Private Limited, best software company in India, software development company india, react development company, AI development company, mobile app development company, cloud computing services" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <link rel="canonical" href="https://www.zustandtech.com/" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="ZustandTech" />
        <meta property="og:title" content="ZustandTech | Global Software Development Company" />
        <meta
          property="og:description"
          content="Global software development company specializing in React.js, Node.js, AI solutions, cloud systems, SaaS development, and enterprise software solutions."
        />
        <meta property="og:image" content="https://www.zustandtech.com/ar.png" />
        <meta property="og:url" content="https://www.zustandtech.com/" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ZustandTech | Global Software Development Company" />
        <meta
          name="twitter:description"
          content="React.js, AI, cloud, mobile apps, and enterprise software development company."
        />
        <meta name="twitter:image" content="https://www.zustandtech.com/ar.png" />
      </Helmet>
      <Navbar />
      <Outlet />
      <Footer />
      <PopupInquiry />
      <WhatsAppButton />
    </>
  );
}

// Admin Layout Component
function AdminLayout({ children }) {
  return (
    <div className="admin-layout">
      {children}
    </div>
  );
}

function AppRoutes() {
  const { loading, isAuthenticated, user } = useAuth();
  
  // ✅ Console log removed
  
  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Loading application...</p>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Admin Login - Public (NO PROTECTION) */}
      <Route path="/admin/login" element={<AdminLogin />} />
      
      {/* Protected Admin Routes - ONLY accessible after login */}
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin/users" 
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/admin/inquiries" 
        element={
          <ProtectedRoute>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        } 
      />
      
      {/* Redirect /admin/* to dashboard if authenticated, else to login */}
      <Route 
        path="/admin/*" 
        element={
          <Navigate to="/admin/dashboard" replace />
        } 
      />
      
      {/* Public Routes */}
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="services" element={<ServicesPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="blog" element={<BlogPage />} />
        <Route path="blog/:slug" element={<BlogDetail />} />
        <Route path="careers" element={<CareerPage />} />
        <Route path="apply/:slug" element={<ApplyPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="faq" element={<FAQPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="sitemap" element={<SitemapPage />} />
        <Route path="/watch-demo" element={<WatchDemo />} />
      </Route>
      
      {/* 404 - Redirect to Home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <div className="App">
          <AppRoutes />
          {/* AuthDebug removed from production */}
          {/* {process.env.NODE_ENV === 'development' && <AuthDebug />} */}
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;